'use client';

import { useState, useEffect } from 'react';
import { 
  Users, UserPlus, TrendingDown, GraduationCap,
  BookOpen, Search, FileText, Calendar, Filter,
  Eye, Edit, Mail, Phone, MapPin, Star, Square,
  TreePine, HelpCircle, Zap
} from 'lucide-react';
import {
  hrMetrics,
  onboardingProgress,
  recentActivities,
  quickActions,
  upcomingEvents,
  getGlobalHRMetrics,
  getAllEmployeePerformance
} from '../data';
import { 
  getAllMonumentaEmployees,
  mapEmployeePerformanceToCategory,
  formatCurrency,
  type MonumentaEmployee
} from '@/lib/real-data-service';

export function DashboardRHTab() {
  const [selectedClient, setSelectedClient] = useState('todos');
  const [selectedArea, setSelectedArea] = useState('todos');
  const [selectedSubArea, setSelectedSubArea] = useState('todos');
  const [selectedPeriod, setSelectedPeriod] = useState('2024');
  const [date, setDate] = useState<Date | undefined>(new Date());

  // Estados para lista de colaboradores
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('todos');
  const [selectedPerformance, setSelectedPerformance] = useState('todos');
  const [selectedContract, setSelectedContract] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [showEmployeeList, setShowEmployeeList] = useState(false);
  const itemsPerPage = 10;

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [realHRMetrics, setRealHRMetrics] = useState<any>(null);
  const [allEmployees, setAllEmployees] = useState<MonumentaEmployee[]>([]);

  // Load data asynchronously
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [metrics, employees] = await Promise.all([
          getGlobalHRMetrics(), // Use global metrics for Dashboard RH
          getAllMonumentaEmployees()
        ]);

        setRealHRMetrics(metrics);
        setAllEmployees(employees);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtrar colaboradores baseado nos filtros selecionados
  const filteredEmployees = allEmployees.filter(employee => {
    const matchesSearch = searchTerm === '' || 
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = selectedDepartment === 'todos' || 
      employee.department.toLowerCase() === selectedDepartment.toLowerCase();

    const matchesPerformance = selectedPerformance === 'todos' || 
      mapEmployeePerformanceToCategory(employee.performance).toLowerCase() === selectedPerformance.toLowerCase();

    const matchesContract = selectedContract === 'todos' || 
      employee.contractType.toLowerCase() === selectedContract.toLowerCase();

    return matchesSearch && matchesDepartment && matchesPerformance && matchesContract;
  });

  // Paginação
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Reset página quando filtros mudam
  const handleFilterChange = (filterType: string, value: string) => {
    setCurrentPage(1);
    switch (filterType) {
      case 'department':
        setSelectedDepartment(value);
        break;
      case 'performance':
        setSelectedPerformance(value);
        break;
      case 'contract':
        setSelectedContract(value);
        break;
    }
  };

  // Função para obter ícone de performance
  const getPerformanceIcon = (performance: string) => {
    const category = mapEmployeePerformanceToCategory(performance);
    switch (category) {
      case 'Alta Performance': return Star;
      case 'Potencial Crescimento': return Square;
      case 'Desenvolvimento': return BookOpen;
      case 'Cumpre Função': return TreePine;
      case 'Sob Análise': return HelpCircle;
      case 'Desalinhado': return Zap;
      default: return Users;
    }
  };

  // Função para obter cor de performance
  const getPerformanceColor = (performance: string) => {
    const category = mapEmployeePerformanceToCategory(performance);
    switch (category) {
      case 'Alta Performance': return 'text-yellow-400 bg-yellow-400/20';
      case 'Potencial Crescimento': return 'text-blue-400 bg-blue-400/20';
      case 'Desenvolvimento': return 'text-green-400 bg-green-400/20';
      case 'Cumpre Função': return 'text-gray-400 bg-gray-400/20';
      case 'Sob Análise': return 'text-yellow-500 bg-yellow-500/20';
      case 'Desalinhado': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="metric-card animate-pulse">
              <div className="h-6 bg-brand-gray bg-opacity-20 rounded mb-2"></div>
              <div className="h-8 bg-brand-gray bg-opacity-20 rounded mb-2"></div>
              <div className="h-4 bg-brand-gray bg-opacity-20 rounded"></div>
            </div>
          ))}
        </div>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          <p className="text-brand-gray mt-2">Carregando dados...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-red-400 mb-2">Erro ao carregar dados</div>
          <p className="text-brand-gray">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary mt-4"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Data not loaded yet
  if (!realHRMetrics) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-brand-gray">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="metric-card group">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-brand-primary" size={24} />
          </div>
          <div className="metric-value">{realHRMetrics.totalColaboradores.value}</div>
          <div className="metric-label">TOTAL DE COLABORADORES</div>
          <div className={`text-sm mt-2 ${realHRMetrics.totalColaboradores.positive ? 'text-success' : 'text-error'}`}>
            {realHRMetrics.totalColaboradores.trend}
          </div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-4">
            <UserPlus className="text-brand-primary" size={24} />
          </div>
          <div className="metric-value">{realHRMetrics.novosColaboradores.value}</div>
          <div className="metric-label">NOVOS COLABORADORES</div>
          <div className="text-sm text-brand-gray mt-2">{realHRMetrics.novosColaboradores.trend}</div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-4">
            <TrendingDown className="text-brand-primary" size={24} />
          </div>
          <div className="metric-value">{realHRMetrics.taxaTurnover.value}%</div>
          <div className="metric-label">TAXA DE TURNOVER</div>
          <div className={`text-sm mt-2 ${realHRMetrics.taxaTurnover.positive ? 'text-success' : 'text-error'}`}>
            {realHRMetrics.taxaTurnover.trend}
          </div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-4">
            <GraduationCap className="text-brand-primary" size={24} />
          </div>
          <div className="metric-value">{realHRMetrics.horasTreinamento.value}</div>
          <div className="metric-label">HORAS DE TREINAMENTO</div>
          <div className={`text-sm mt-2 ${realHRMetrics.horasTreinamento.positive ? 'text-success' : 'text-error'}`}>
            {realHRMetrics.horasTreinamento.trend}
          </div>
        </div>
      </div>

      {/* Layout Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna Esquerda */}
        <div className="space-y-6 flex flex-col">
          {/* Onboardings em Andamento */}
          <div className="card flex-1">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Onboardings em Andamento
            </h3>
            <div className="space-y-4">
              {onboardingProgress.map((item) => (
                <div key={item.id} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-brand-dark">
                      {item.avatar}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <p className="font-medium text-white">{item.name}</p>
                      <span className="text-sm text-brand-primary">{item.progress}%</span>
                    </div>
                    <p className="text-sm text-brand-gray mb-2">{item.role}</p>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Atividade Recente */}
          <div className="card flex-1">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Atividade Recente
            </h3>
            <div className="space-y-4">
              {recentActivities.map((activity) => {
                const Icon = activity.icon;
                return (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className={`p-1 rounded ${activity.color.replace('text-', 'bg-').replace('-500', '-500/20')}`}>
                      <Icon className={`w-4 h-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Coluna Direita */}
        <div className="space-y-6 flex flex-col">
          {/* Ações Rápidas */}
          <div className="card">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Ações Rápidas
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    className="btn-secondary h-auto p-4 flex flex-col items-center gap-2"
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-sm">{action.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Próximos Eventos */}
          <div className="card flex-1">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Próximos Eventos
            </h3>
            <div className="space-y-4">
              {upcomingEvents.map((event) => {
                const Icon = event.icon;
                return (
                  <div key={event.id} className="flex items-center gap-3">
                    <div className={`p-2 rounded ${event.color.replace('text-', 'bg-').replace('-500', '-500/20')}`}>
                      <Icon className={`w-4 h-4 ${event.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white">{event.title}</p>
                      <p className="text-xs text-gray-500">{event.date}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Calendário Gerencial */}
      <div className="card">
        <h3 className="font-display text-lg font-semibold text-white mb-4">
          Calendário Gerencial
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex items-center justify-center p-8 bg-brand-dark rounded-monumenta border border-gray-700">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-brand-primary mx-auto mb-4" />
              <p className="text-white font-medium">Calendário</p>
              <p className="text-brand-gray text-sm">Visualização de eventos</p>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-medium text-white">Legendas</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                <span className="text-sm text-gray-300">Aniversários</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm text-gray-300">Férias</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-300">Treinamentos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-300">Reuniões 1:1</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm text-gray-300">Avaliações</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Todos os Colaboradores */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-2">
              Todos os Colaboradores
            </h3>
            <p className="text-brand-gray text-sm">
              {filteredEmployees.length} de {allEmployees.length} colaboradores
            </p>
          </div>
          <button
            onClick={() => setShowEmployeeList(!showEmployeeList)}
            className="btn-secondary flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            {showEmployeeList ? 'Ocultar Lista' : 'Ver Lista Completa'}
          </button>
        </div>

        {showEmployeeList && (
          <>
            {/* Filtros da Lista de Colaboradores */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 p-4 bg-brand-dark-light rounded-monumenta">
              <div>
                <label className="form-label">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Nome, cargo ou email..."
                    className="form-input w-full pl-10"
                    style={{ paddingLeft: '3rem' }}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Departamento</label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="form-input w-full"
                >
                  <option value="todos">Todos</option>
                  {Array.from(new Set(allEmployees.map(emp => emp.department))).map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Performance</label>
                <select
                  value={selectedPerformance}
                  onChange={(e) => handleFilterChange('performance', e.target.value)}
                  className="form-input w-full"
                >
                  <option value="todos">Todas</option>
                  <option value="alta performance">Alta Performance</option>
                  <option value="potencial crescimento">Potencial Crescimento</option>
                  <option value="desenvolvimento">Desenvolvimento</option>
                  <option value="cumpre função">Cumpre Função</option>
                  <option value="sob análise">Sob Análise</option>
                  <option value="desalinhado">Desalinhado</option>
                </select>
              </div>
              <div>
                <label className="form-label">Contrato</label>
                <select
                  value={selectedContract}
                  onChange={(e) => handleFilterChange('contract', e.target.value)}
                  className="form-input w-full"
                >
                  <option value="todos">Todos</option>
                  <option value="PJ">PJ</option>
                  <option value="CLT">CLT</option>
                  <option value="SOCIO">Sócio</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDepartment('todos');
                    setSelectedPerformance('todos');
                    setSelectedContract('todos');
                    setCurrentPage(1);
                  }}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                >
                  <Filter className="w-4 h-4" />
                  Limpar
                </button>
              </div>
            </div>

            {/* Tabela de Colaboradores */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Colaborador</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Departamento</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Cargo</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Performance</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Contrato</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Custo</th>
                    <th className="text-left text-brand-gray text-sm font-medium py-3 px-4">Férias</th>
                    <th className="text-right text-brand-gray text-sm font-medium py-3 px-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((employee, index) => {
                    const PerformanceIcon = getPerformanceIcon(employee.performance);
                    const performanceColor = getPerformanceColor(employee.performance);
                    const performanceCategory = mapEmployeePerformanceToCategory(employee.performance);
                    
                    return (
                      <tr key={employee.id} className="border-b border-gray-800 hover:bg-brand-dark-light/50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center">
                              <span className="text-xs font-bold text-brand-dark">
                                {employee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{employee.name}</p>
                              <p className="text-brand-gray text-xs">{employee.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white text-sm">{employee.department}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white text-sm">{employee.role}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${performanceColor}`}>
                              <PerformanceIcon className="w-3 h-3" />
                            </div>
                            <span className="text-white text-sm">{performanceCategory}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            employee.contractType === 'PJ' ? 'bg-blue-500/20 text-blue-400' :
                            employee.contractType === 'CLT' ? 'bg-green-500/20 text-green-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {employee.contractType}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white text-sm">{formatCurrency(employee.totalCost)}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-brand-gray text-sm">{employee.vacationBalance} dias</span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button className="p-1 text-brand-gray hover:text-brand-primary transition-colors">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-brand-gray hover:text-brand-primary transition-colors">
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-brand-gray text-sm">
                  Mostrando {startIndex + 1} a {Math.min(endIndex, filteredEmployees.length)} de {filteredEmployees.length} colaboradores
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNumber = i + 1;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            currentPage === pageNumber
                              ? 'bg-brand-primary text-brand-dark'
                              : 'text-brand-gray hover:text-white hover:bg-brand-dark'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <>
                        <span className="text-brand-gray px-2">...</span>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            currentPage === totalPages
                              ? 'bg-brand-primary text-brand-dark'
                              : 'text-brand-gray hover:text-white hover:bg-brand-dark'
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}